import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Wrench, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GarageLogin = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Login form
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  // Register form
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    mobile: '',
    address: '',
    city: 'Bangalore',
    gst_number: '',
    owner_name: '',
    owner_pin: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/garage/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        localStorage.setItem('garage_token', data.token);
        localStorage.setItem('garage_profile', JSON.stringify(data.garage));
        toast.success('Login successful!');
        navigate('/garage/dashboard');
      } else {
        toast.error(data.detail || 'Login failed');
      }
    } catch (error) {
      toast.error('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    if (registerData.owner_pin.length < 4) {
      toast.error('Owner PIN must be at least 4 digits');
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/garage/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        localStorage.setItem('garage_token', data.token);
        localStorage.setItem('garage_profile', JSON.stringify(data.garage));
        toast.success('Registration successful!');
        navigate('/garage/dashboard');
      } else {
        toast.error(data.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800/50 border-slate-700 backdrop-blur-sm">
        <CardHeader className="text-center">
          <div className="mx-auto w-24 h-24 rounded-2xl overflow-hidden mb-4">
            <img 
              src="/pitcrew-logo.png" 
              alt="Pitcrew Logo" 
              className="w-full h-full object-cover"
            />
          </div>
          <CardTitle className="text-2xl">
            <span className="text-white">Pitcrew</span>
            <span className="text-red-500"> GMS</span>
          </CardTitle>
          <CardDescription className="text-slate-400">
            powered by <span className="text-orange-400">Propitstop</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-slate-700/50">
              <TabsTrigger value="login" className="data-[state=active]:bg-orange-600">Login</TabsTrigger>
              <TabsTrigger value="register" className="data-[state=active]:bg-orange-600">Register</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="login-email" className="text-slate-300">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    className="bg-slate-700/50 border-slate-600 text-white"
                    placeholder="garage@example.com"
                    required
                    data-testid="login-email"
                  />
                </div>
                <div className="relative">
                  <Label htmlFor="login-password" className="text-slate-300">Password</Label>
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    className="bg-slate-700/50 border-slate-600 text-white pr-10"
                    placeholder="••••••••"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-8 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-3 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-sm">Garage Name</Label>
                    <Input
                      value={registerData.name}
                      onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                      className="bg-slate-700/50 border-slate-600 text-white"
                      placeholder="Your Garage"
                      required
                      data-testid="register-name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Owner Name</Label>
                    <Input
                      value={registerData.owner_name}
                      onChange={(e) => setRegisterData({...registerData, owner_name: e.target.value})}
                      className="bg-slate-700/50 border-slate-600 text-white"
                      placeholder="Owner"
                      required
                      data-testid="register-owner"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Email</Label>
                  <Input
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                    className="bg-slate-700/50 border-slate-600 text-white"
                    placeholder="garage@example.com"
                    required
                    data-testid="register-email"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-sm">Password</Label>
                    <Input
                      type="password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                      className="bg-slate-700/50 border-slate-600 text-white"
                      placeholder="••••••••"
                      required
                      data-testid="register-password"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Mobile</Label>
                    <Input
                      value={registerData.mobile}
                      onChange={(e) => setRegisterData({...registerData, mobile: e.target.value})}
                      className="bg-slate-700/50 border-slate-600 text-white"
                      placeholder="9876543210"
                      maxLength={10}
                      required
                      data-testid="register-mobile"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Address</Label>
                  <Input
                    value={registerData.address}
                    onChange={(e) => setRegisterData({...registerData, address: e.target.value})}
                    className="bg-slate-700/50 border-slate-600 text-white"
                    placeholder="Street, Area, City"
                    required
                    data-testid="register-address"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-sm">GST Number (Optional)</Label>
                    <Input
                      value={registerData.gst_number}
                      onChange={(e) => setRegisterData({...registerData, gst_number: e.target.value})}
                      className="bg-slate-700/50 border-slate-600 text-white"
                      placeholder="29ABCDE1234F1Z5"
                      data-testid="register-gst"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Owner PIN (4-6 digits)</Label>
                    <Input
                      type="password"
                      value={registerData.owner_pin}
                      onChange={(e) => setRegisterData({...registerData, owner_pin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      className="bg-slate-700/50 border-slate-600 text-white"
                      placeholder="••••"
                      required
                      data-testid="register-pin"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? 'Registering...' : 'Register Garage'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default GarageLogin;
